import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Shield, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = '';

export default function TwoFactorSection() {
  const [status, setStatus] = useState(null);
  const [step, setStep] = useState('idle');
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [disablePw, setDisablePw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [loading, setLoading] = useState(false);
  const token = sessionStorage.getItem('session_token');
  const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };

  const loadStatus = () => {
    fetch(`${BACKEND_URL}/api/auth/2fa/status`, { credentials: 'include', headers })
      .then(r => r.json()).then(setStatus).catch(() => {});
  };
  useEffect(() => { loadStatus(); }, []);

  if (!status) return null;

  const handleSetup = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/2fa/setup`, {
        method: 'POST', credentials: 'include', headers,
      });
      if (!res.ok) throw new Error('Failed to set up 2FA');
      const data = await res.json();
      setQrCode(data.qr_code);
      setSecret(data.secret);
      setStep('verify');
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  const handleSetPassword = async () => {
    if (!newPw || newPw.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (newPw !== confirmPw) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/set-password`, {
        method: 'POST', credentials: 'include', headers,
        body: JSON.stringify({ new_password: newPw }),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Failed'); }
      toast.success('Password set! You can now enable 2FA.');
      setNewPw(''); setConfirmPw(''); setStep('idle');
      loadStatus();
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  const handleVerify = async () => {
    if (!code || code.length !== 6) { toast.error('Enter the 6-digit code from your authenticator app'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/2fa/enable`, {
        method: 'POST', credentials: 'include', headers,
        body: JSON.stringify({ code }),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Invalid code'); }
      toast.success('Two-factor authentication enabled!');
      setStatus({ ...status, enabled: true });
      setStep('idle'); setCode(''); setQrCode(''); setSecret('');
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  const handleDisable = async () => {
    if (!disablePw) { toast.error('Enter your password to disable 2FA'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/2fa/disable`, {
        method: 'POST', credentials: 'include', headers,
        body: JSON.stringify({ password: disablePw }),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Failed'); }
      toast.success('Two-factor authentication disabled');
      setStatus({ ...status, enabled: false });
      setStep('idle'); setDisablePw('');
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  return (
    <Card className="p-6 border border-slate-200 mt-4" data-testid="two-factor-section">
      <h3 className="text-sm font-bold font-heading mb-4 text-slate-500 uppercase tracking-wide flex items-center gap-2">
        {status.enabled ? <ShieldCheck className="h-4 w-4 text-brand-sage" /> : <Shield className="h-4 w-4" />}
        Two-Factor Authentication
        {status.enabled && <span className="text-[10px] font-medium text-brand-sage bg-brand-sage/10 px-2 py-0.5 rounded-full ml-1">Enabled</span>}
      </h3>

      {step === 'idle' && !status.enabled && (
        <div>
          <p className="text-sm text-slate-500 mb-3">Add an extra layer of security to your account using an authenticator app like Google Authenticator or Authy.</p>
          {status.available ? (
            <Button data-testid="setup-2fa-btn" onClick={handleSetup} disabled={loading}
              size="sm" className="bg-brand-navy hover:bg-brand-dark text-white">
              {loading ? 'Setting up...' : 'Set Up 2FA'}
            </Button>
          ) : (
            <div>
              <p className="text-sm text-amber-600 mb-3">You signed in with Google. Set a password first to enable 2FA.</p>
              <Button data-testid="set-password-for-2fa" onClick={() => setStep('set-password')}
                size="sm" className="bg-brand-navy hover:bg-brand-dark text-white">
                Set Password
              </Button>
            </div>
          )}
        </div>
      )}

      {step === 'idle' && status.enabled && (
        <div>
          <p className="text-sm text-slate-500 mb-3">Two-factor authentication is active. You'll need your authenticator app each time you log in.</p>
          <Button data-testid="disable-2fa-trigger" onClick={() => setStep('disable')}
            size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50">
            Disable 2FA
          </Button>
        </div>
      )}

      {step === 'verify' && (
        <div className="space-y-4" data-testid="2fa-setup-form">
          <p className="text-sm text-slate-600">Scan this QR code with your authenticator app:</p>
          <div className="flex justify-center">
            {qrCode && <img src={qrCode} alt="2FA QR Code" className="w-48 h-48 rounded-lg border border-slate-200" />}
          </div>
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-xs text-slate-500 mb-1">Can't scan? Enter this key manually:</p>
            <code data-testid="2fa-secret-key" className="text-xs font-mono text-brand-navy bg-white px-2 py-1 rounded border select-all">{secret}</code>
          </div>
          <div className="max-w-xs">
            <p className="text-sm text-slate-600 mb-2">Enter the 6-digit code from your app:</p>
            <Input data-testid="2fa-verify-code" type="text" inputMode="numeric" maxLength={6}
              placeholder="000000" value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
              className="h-11 text-center text-lg tracking-widest font-mono border-slate-200" />
          </div>
          <div className="flex gap-2">
            <Button data-testid="2fa-confirm-btn" onClick={handleVerify} disabled={loading || code.length !== 6}
              size="sm" className="bg-brand-navy hover:bg-brand-dark text-white">
              {loading ? 'Verifying...' : 'Verify & Enable'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setStep('idle'); setCode(''); }}>Cancel</Button>
          </div>
        </div>
      )}

      {step === 'disable' && (
        <div className="space-y-3 max-w-sm" data-testid="2fa-disable-form">
          <p className="text-sm text-slate-600">Enter your password to disable two-factor authentication:</p>
          <Input data-testid="2fa-disable-password" type="password" placeholder="Current password"
            value={disablePw} onChange={e => setDisablePw(e.target.value)} className="h-10 border-slate-200" />
          <div className="flex gap-2">
            <Button data-testid="2fa-disable-confirm" onClick={handleDisable} disabled={loading}
              size="sm" className="bg-red-600 hover:bg-red-700 text-white">
              {loading ? 'Disabling...' : 'Disable 2FA'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setStep('idle'); setDisablePw(''); }}>Cancel</Button>
          </div>
        </div>
      )}

      {step === 'set-password' && (
        <div className="space-y-3 max-w-sm" data-testid="set-password-form">
          <p className="text-sm text-slate-600">Create a password for your account to enable 2FA:</p>
          <Input data-testid="set-pw-new" type="password" placeholder="New password (min 6 chars)"
            value={newPw} onChange={e => setNewPw(e.target.value)} className="h-10 border-slate-200" />
          <Input data-testid="set-pw-confirm" type="password" placeholder="Confirm password"
            value={confirmPw} onChange={e => setConfirmPw(e.target.value)} className="h-10 border-slate-200" />
          <div className="flex gap-2">
            <Button data-testid="set-pw-submit" onClick={handleSetPassword} disabled={loading}
              size="sm" className="bg-brand-navy hover:bg-brand-dark text-white">
              {loading ? 'Setting...' : 'Set Password'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setStep('idle'); setNewPw(''); setConfirmPw(''); }}>Cancel</Button>
          </div>
        </div>
      )}
    </Card>
  );
}
